import { Button, DialogContent, Stack, TextField, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import NoteClient, { NoteErrors } from "../apiClients/NoteClient";
import { UpsertAction } from "../Enums";
import { Note, NoteAssociationType } from "../models/dtos";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import { IsEmpty } from "../Helpers";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { I18N } from "../I18N";
import { BiMessageRoundedAdd } from 'react-icons/bi'
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface NoteUpsertPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    title?: string;
    associationId?: string;
    associationType?: NoteAssociationType;
    noteId?: string;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    onClose?: () => void;
    icon?: JSX.Element;
}

const NoteUpsertPopup = ({ mode, title = I18N.Translate("Notes"), associationId, associationType, noteId, onChange = async () => { }, onAdd = async () => { }, onClose = () => { }, icon = <BiMessageRoundedAdd size={25} />, ...btnProps }: NoteUpsertPopupProps) => {

    const newNote = NoteClient.CreateDefaultNote();

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    const initialized = useRef<boolean>();
    const theme = useTheme();

    const [note, setNote] = useState<Note>(newNote);
    const [dataHasChanged, setDataHasChanged] = useState<boolean>(false);

    const loadData = async () => {
        try {
            setBusy(true);

            switch (mode) {
                case UpsertAction.Create:
                    setNote(newNote);
                    break;
                case UpsertAction.Update:
                    let n = await new NoteClient().GetSingle(noteId);
                    setNote(n);
                    setDataHasChanged(false);
                    break;
                default:
                    break;
            }
        }
        catch (error) {
            setError(error, NoteErrors.GetSingle);
        }
        finally {
            initialized.current = true;
            setBusy(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const handleClose = () => {
        setOpen(false);
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                let createNote = {
                    AssociationId: associationId,
                    AssociationType: associationType,
                    Title: note.Title,
                    Content: note.Content
                }

                let id = await new NoteClient().Create(createNote);
                setDataHasChanged(false);

                setOpen(false);
                onAdd(id);
                onClose();
            }

        } catch (error) {
            setError(error, NoteErrors.Create);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setNote({ ...note, [name]: value })
        setDataHasChanged(true);
    };

    return (
        <>
            <ErrorDisplay />
            <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>
            <ResponsiveDialog aria-labelledby="note-upsert-title" open={open} fullWidth onClose={handleClose} tabletFullScreen="portrait" PaperProps={{ sx: { overflow: 'hidden', '& > div': { flex: '1 1 auto', minHeight: 0, height: 'auto !important', display: 'flex', flexDirection: 'column' } } }}>
                <BusySpinner busy={busy}>
                    <ResponsiveDialogHeader
                        id="note-upsert-title"
                        title={<Stack direction="row" spacing={1.5} alignItems="center"><BiMessageRoundedAdd size='30px' />{title}</Stack>}
                        onClose={handleClose}
                    />
                    <DialogContent dividers sx={{ overflowY: 'auto' }}>
                        <MobileFormStack tabletColumns={1} desktopColumns={1} sx={{ gap: 2 }}>
                            <TextField label="Title" fullWidth
                                name="Title"
                                size="small"
                                variant="outlined"
                                error={IsEmpty(note.Title)}
                                value={note.Title}
                                onChange={handleInputChange} />
                            <TextField label="Note" fullWidth
                                name="Content"
                                size="small"
                                variant="outlined"
                                multiline
                                rows={8}
                                error={IsEmpty(note.Content)}
                                value={note.Content}
                                onChange={handleInputChange} />
                        </MobileFormStack>
                    </DialogContent>
                    <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                        <Button onClick={handleClose} variant='text' color='inherit'>Cancel</Button>
                        <Button disabled={!dataHasChanged} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                    </StickyDialogActions>
                </BusySpinner>
            </ResponsiveDialog>
        </>
    )
};

export default NoteUpsertPopup;
